import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { VendorPayoffPage } from './VendorPayoffPage';
import { getVendorPayoffData, sendPayoffToSpruce } from 'store/vendorStore/vendorPayoffData/dispatch';
import { getVendorDocuments } from 'store/vendorStore/vendorDocuments/dispatch';
import { getVendorCommentsData } from 'store/vendorStore/vendorComments/dispatch';
import { eVendorServiceType } from '../../../types/order/title/eVendorServiceType';
import { createDefaultVendorPayoffInformationData } from '../../../types/vendorDashboard/IVendorPayoffInformation';
import { ClientUrlFactory } from '../../../ui-toolkit/rest/ClientUrlFactory';
import { VendorSelectors } from '../../../selectors/vendorPortal/VendorSelectors';
import { useParams } from 'react-router-dom';
import { UUID } from '../../../types/shared/UUID';
import { VendorWrapper } from '../../../components/vendorDashboard/VendorWrapper';
import { VendorRedirectGuard } from '../../../components/vendorDashboard/VendorRedirectGuard';
import { findError } from '../../../utilities/vendorDashboard/findError';
import { isLoading } from '../../../utilities/vendorDashboard/isLoading';
import { useAppDispatch } from '../../../store/store';

interface IVendorPayoffContainerProps {}

export const VendorPayoffContainer: React.FC<IVendorPayoffContainerProps> = () => {
  const { orderId } = useParams<{ orderId: UUID }>();
  const { payoff, payoffError, payoffFetchStatus, sentToSpruceError, sentToSpruceFetchStatus, updatePayoffError } =
    useSelector(VendorSelectors.selectPayoff);
  const { documents, documentsError } = useSelector(VendorSelectors.selectDocuments);
  const { getCommentsFetchStatus, getCommentsError, postCommentError, postCommentFetchStatus } = useSelector(
    VendorSelectors.selectComments
  );
  const [updateInformation, setUpdateInformation] = useState(false);
  const [redirectToPayoffsPage, setRedirectToOrdersPage] = useState(false);

  const pageError = useMemo(
    () =>
      findError(payoffError, documentsError, getCommentsError, postCommentError, sentToSpruceError, updatePayoffError),
    [payoffError, documentsError, getCommentsError, postCommentError, sentToSpruceError, updatePayoffError]
  );

  const pageLoading = useMemo(
    () => isLoading(payoffFetchStatus.fetching, getCommentsFetchStatus.fetching, sentToSpruceFetchStatus.fetching),
    [payoffFetchStatus.fetching, getCommentsFetchStatus.fetching, sentToSpruceFetchStatus.fetching]
  );

  const dispatch = useAppDispatch();
  useEffect(() => {
    if (!updateInformation) {
      // update info if user return from another page
      setUpdateInformation(true);
      dispatch(getVendorPayoffData(orderId));
      dispatch(getVendorDocuments(orderId));
      dispatch(getVendorCommentsData(orderId, [eVendorServiceType.payoffRequest]));
    }
  }, [dispatch, orderId, updateInformation]);

  const onSendToSpruce = async () => {
    try {
      dispatch(sendPayoffToSpruce(orderId, payoff[orderId].payoffRequestId));
      window.setTimeout(() => setRedirectToOrdersPage(true), 3000);
    } catch (e: any) {}
  };

  return (
    <VendorRedirectGuard redirect={redirectToPayoffsPage} to={ClientUrlFactory().urlFor.vendorPayoffs()}>
      <VendorWrapper isLoading={pageLoading}>
        <VendorPayoffPage
          orderId={orderId}
          payoff={payoff[orderId] || createDefaultVendorPayoffInformationData()}
          pageError={pageError}
          documents={documents[orderId] || []}
          uploadNewCommentFetch={postCommentFetchStatus}
          onSendToSpruce={onSendToSpruce}
          sendToSpruceFetchStatus={sentToSpruceFetchStatus}
          sendToSpruceError={sentToSpruceError}
        />
      </VendorWrapper>
    </VendorRedirectGuard>
  );
};
